import { useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import moment from "moment-timezone";

import { Identity } from "src/types/identity";
import { identify } from "src/redux/main/mainActions";
import { RootState } from "src/redux/reducers";
import { usePromiseDispatch } from "src/hooks/usePromiseDispatch";

const REFRESH_BEFORE_MS = 5 * 60 * 1000;

export function useJwtRefresh() {
  const promiseDispatch = usePromiseDispatch();
  const jwtToken = useSelector(
    (state: RootState) => state.mainReducer.identity?.jwtToken,
  );
  const activeWorkspaceUuid = useSelector(
    (state: RootState) => state.mainReducer.activeWorkspaceUuid,
  );

  const refreshPromiseRef = useRef<Promise<string> | null>(null);

  const getValidToken = useCallback(async (): Promise<string> => {
    const expiresAt = jwtToken?.expiresAt;

    if (
      jwtToken &&
      expiresAt &&
      moment
        .tz(expiresAt.date, "YYYY-MM-DD HH:mm:ss.SSSSSS", expiresAt.timezone)
        .diff(moment()) > REFRESH_BEFORE_MS
    ) {
      return jwtToken.token;
    }

    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const refreshPromise = promiseDispatch<Identity>(
      identify(activeWorkspaceUuid),
    )
      .then((result) => result.payload.data.content.jwtToken.token)
      .finally(() => {
        refreshPromiseRef.current = null;
      });

    refreshPromiseRef.current = refreshPromise;

    return refreshPromise;
  }, [jwtToken, activeWorkspaceUuid, promiseDispatch]);

  return { getValidToken };
}
